import { Button, useMantineTheme, type MantineSize } from '@mantine/core';
import { ChevronDown, X, type Icon as FeatherIcon } from 'react-feather';
import type { ComponentPropsWithoutRef } from 'react';
import { type CustomIconName, Icon } from '@/components/Icon/Icon';

type Variant = 'primary' | 'secondary' | 'tertiary' | 'filter' | 'pill';

interface ButtonProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'disabled' | 'type' | 'id' | 'children' | 'onClick'
> {
  variant?: Variant;
  size?: MantineSize;
  id?: string;
  label: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  leftIconName?: CustomIconName;
  leftIconComponent?: FeatherIcon;
  isSelected?: boolean;
  isDropDown?: boolean;
  isToggle?: boolean;
}

const getIconColor = (
  variant: Variant,
  theme: ReturnType<typeof useMantineTheme>,
  isDisabled: boolean,
  isActive: boolean
) => {
  if (variant === 'filter') {
    return isActive
      ? theme.other.mainColor.foreground.onAccent
      : theme.other.mainColor.foreground.primary;
  }
  if (variant === 'primary') return theme.other.mainColor.foreground.onAccent;
  if (isDisabled) return theme.other.mainColor.foreground.tertiary;
  return theme.other.mainColor.accent.normal;
};

export const ButtonComponent = ({
  variant = 'primary',
  size = 'md',
  type = 'button',
  label,
  id,
  isDisabled = false,
  isLoading = false,
  onClick,
  isDropDown = false,
  isSelected = false,
  isToggle = false,
  leftIconComponent,
  leftIconName,
  ...props
}: ButtonProps) => {
  // @deprecated: Use Mantine `Button` with a theme variant instead.
  const theme = useMantineTheme();
  const iconColor = getIconColor(variant, theme, isDisabled, isSelected);

  const rightIcon = isDropDown ? (
    <ChevronDown size={14} color={iconColor} />
  ) : (
    isSelected && isToggle && <Icon Component={X} color={iconColor} size={10} />
  );

  return (
    <Button
      variant={variant}
      size={size}
      type={type}
      id={id}
      data-selected={isSelected}
      leftSection={
        (leftIconName ?? leftIconComponent) && (
          <>
            {leftIconName && (
              <Icon name={leftIconName} size={16} color={iconColor} />
            )}
            {leftIconComponent && (
              <Icon Component={leftIconComponent} size={16} color={iconColor} />
            )}
          </>
        )
      }
      rightSection={rightIcon || undefined}
      disabled={isDisabled}
      loading={isLoading}
      onClick={onClick}
      {...props}
    >
      <span>{label}</span>
    </Button>
  );
};
